import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { SocratesLogo } from '@/components/SocratesLogo';
import { useToast } from '@/hooks/use-toast';
import { Loader2, CheckCircle, XCircle, UserPlus } from 'lucide-react';

interface Invite {
  id: string;
  email: string;
  role: string;
  message: string | null;
  expires_at: string;
  accepted_at: string | null;
  league: {
    id: string;
    name: string;
  };
}

export default function AcceptInvite() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, refreshMemberships } = useAuth();

  const [invite, setInvite] = useState<Invite | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Fetch invite details via RPC (bypasses RLS so unauthenticated users can see the invite)
  useEffect(() => {
    const fetchInvite = async () => {
      if (!code) {
        setError('Invalid invite link');
        setLoading(false);
        return;
      }

      const { data, error: rpcError } = await supabase.rpc('get_invite_by_code', {
        p_code: code,
      });

      if (rpcError) {
        console.error('get_invite_by_code error:', rpcError);
        setError('Failed to load invite. Please try again.');
        setLoading(false);
        return;
      }

      const result = data as Record<string, unknown> | null;

      if (result?.error) {
        setError('Invite not found or has expired');
        setLoading(false);
        return;
      }

      // Check if already accepted
      if (result?.accepted_at) {
        setError('This invite has already been used');
        setLoading(false);
        return;
      }

      // Check if expired
      if (new Date(result?.expires_at as string) < new Date()) {
        setError('This invite has expired');
        setLoading(false);
        return;
      }

      setInvite(result as unknown as Invite);
      setLoading(false);
    };

    fetchInvite();
  }, [code]);

  const acceptInvite = async () => {
    if (!invite || !user || !code) return;

    setAccepting(true);

    try {
      const { data, error: rpcError } = await supabase.rpc('accept_league_invite', {
        p_code: code,
      });

      if (rpcError) {
        console.error('accept_league_invite error:', rpcError);
        toast({
          title: 'Failed to join league',
          description: rpcError.message,
          variant: 'destructive',
        });
        setAccepting(false);
        return;
      }

      const result = data as Record<string, unknown> | null;

      if (result?.error === 'already_member') {
        toast({
          title: 'Already a member',
          description: `You are already a member of ${result.league_name}.`,
        });
        navigate('/dashboard');
        return;
      }

      if (result?.error) {
        toast({
          title: 'Failed to join league',
          description: result.error as string,
          variant: 'destructive',
        });
        setAccepting(false);
        return;
      }

      // Refresh memberships in AuthContext so dashboard sees the new membership
      await refreshMemberships();

      setSuccess(true);
      toast({
        title: 'Welcome to the league!',
        description: `You've joined ${result?.league_name}.`,
      });

      // Redirect after a moment
      setTimeout(() => navigate('/dashboard'), 2000);
    } catch (err) {
      toast({
        title: 'Failed to join league',
        description: err instanceof Error ? err.message : 'An unexpected error occurred',
        variant: 'destructive',
      });
    } finally {
      setAccepting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 to-stone-100 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-lg shadow-xl max-w-md w-full text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-stone-400" />
          <p className="mt-4 text-stone-600">Loading invite...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 to-stone-100 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-lg shadow-xl max-w-md w-full text-center">
          <div className="flex justify-center mb-4">
            <div className="bg-red-100 p-3 rounded-full">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-stone-800 mb-2">Invalid Invite</h2>
          <p className="text-stone-600 mb-6">{error}</p>
          <Link to="/login">
            <Button variant="outline" className="w-full">
              Go to login
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 to-stone-100 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-lg shadow-xl max-w-md w-full text-center">
          <div className="flex justify-center mb-4">
            <div className="bg-green-100 p-3 rounded-full">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-stone-800 mb-2">Welcome!</h2>
          <p className="text-stone-600 mb-6">
            You've successfully joined <strong>{invite?.league.name}</strong>.
          </p>
          <Link to="/dashboard">
            <Button className="w-full">
              Go to Dashboard
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Main invite acceptance UI
  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 to-stone-100 flex items-center justify-center p-4">
      <div className="bg-white p-8 rounded-lg shadow-xl max-w-md w-full">
        <div className="text-center mb-6">
          <div className="flex justify-center mb-4">
            <SocratesLogo size="large" />
          </div>
          <h1 className="text-2xl font-bold text-stone-800 font-caveat">You're Invited!</h1>
          <p className="text-stone-600 mt-2">
            Join <strong>{invite?.league.name}</strong>
          </p>
        </div>

        {invite?.message && (
          <div className="bg-stone-50 rounded-lg p-4 mb-6">
            <p className="text-sm text-stone-600 italic">"{invite.message}"</p>
          </div>
        )}

        <div className="space-y-3 mb-6">
          <div className="flex justify-between text-sm">
            <span className="text-stone-500">League:</span>
            <span className="font-medium">{invite?.league.name}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-stone-500">Role:</span>
            <span className="font-medium capitalize">{invite?.role}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-stone-500">Email:</span>
            <span className="font-medium">{invite?.email}</span>
          </div>
        </div>

        {user ? (
          // User is logged in - show accept button
          <div className="space-y-4">
            {user.email?.toLowerCase() === invite?.email.toLowerCase() ? (
              <Button
                onClick={acceptInvite}
                className="w-full"
                disabled={accepting}
              >
                {accepting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Joining...
                  </>
                ) : (
                  <>
                    <UserPlus className="mr-2 h-4 w-4" />
                    Accept & Join League
                  </>
                )}
              </Button>
            ) : (
              <div className="text-center">
                <p className="text-sm text-amber-600 mb-4">
                  This invite is for <strong>{invite?.email}</strong>, but you're logged in as <strong>{user.email}</strong>.
                </p>
                <Button
                  variant="outline"
                  onClick={() => navigate('/login')}
                  className="w-full"
                >
                  Sign in with different account
                </Button>
              </div>
            )}
          </div>
        ) : (
          // User not logged in - show options
          <div className="flex flex-col gap-3">
            <Link to={`/login?redirect=/invite/${code}`}>
              <Button variant="outline" className="w-full">
                I have an account - Sign in
              </Button>
            </Link>
            <Link to={`/register?invite=${code}&email=${encodeURIComponent(invite?.email || '')}`}>
              <Button className="w-full">
                <UserPlus className="mr-2 h-4 w-4" />
                Create account & Join
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
